import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

export default function CheckoutSection1() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    
    // State management
    const [planData, setPlanData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [isAnnual, setIsAnnual] = useState(false); // New: billing cycle toggle
    const [billingInfo, setBillingInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'US'
    });

    // Load plan data from location state or localStorage
    useEffect(() => {
        const data = location.state || JSON.parse(localStorage.getItem('selectedPlan') || '{}');
        if (!data.plan) {
            navigate('/plan-selection');
            return;
        }
        setPlanData(data);
    }, [location.state, navigate]);

    // Handle success/cancel from Stripe
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        const paymentStatus = searchParams.get('payment');
        
        if (sessionId) {
            // Verify the session and redirect to dashboard
            verifySession(sessionId);
        } else if (paymentStatus === 'cancelled') {
            setError('Payment was cancelled. You can try again.');
        } else if (paymentStatus === 'success') {
            setSuccess('Payment successful! Redirecting to dashboard...');
            setTimeout(() => navigate('/dashboard'), 3000);
        }
    }, [searchParams, navigate]);

    // Verify Stripe session
    const verifySession = async (sessionId) => {
        try {
            const { buildUrl } = await import('../../services/api');
            const response = await fetch(buildUrl(`/subscription/verify-session/${sessionId}`), {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
            });

            if (response.ok) {
                const sessionData = await response.json();
                if (sessionData.payment_status === 'paid') {
                    setSuccess('Payment successful! Redirecting to dashboard...');
                    setTimeout(() => navigate('/dashboard'), 3000);
                } else {
                    setError('Payment verification failed. Please contact support.');
                }
            } else {
                setError('Could not verify payment. Please contact support.');
            }
        } catch (error) {
            console.error('Session verification error:', error);
            setError('Could not verify payment. Please contact support.');
        }
    };

    // Handle billing info changes
    const handleBillingChange = (e) => {
        const { name, value } = e.target;
        setBillingInfo(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Get plan details with pricing based on billing cycle
    const getPlanDetails = () => {
        if (!planData) return {};
        
        if (planData.plan === 'family') {
            const monthlyPrice = 40.00;
            const annualPrice = monthlyPrice * 12;
            return {
                name: 'Family Plan',
                monthlyPrice: monthlyPrice,
                annualPrice: annualPrice,
                price: isAnnual ? annualPrice : monthlyPrice,
                billingCycle: isAnnual ? 'year' : 'month',
                description: 'Up to 5 members under one subscription',
                features: [
                    'Includes 5 seats',
                    'All Individual features',
                    'Share with family members'
                ],
                seats: 5,
            };
        } else {
            const monthlyPrice = 15.00;
            const annualPrice = monthlyPrice * 12;
            return {
                name: 'Individual Plan',
                monthlyPrice: monthlyPrice,
                annualPrice: annualPrice,
                price: isAnnual ? annualPrice : monthlyPrice,
                billingCycle: isAnnual ? 'year' : 'month',
                description: 'Perfect for personal use and individual productivity',
                features: [
                    'Unlimited AI-powered organization',
                    '10GB cloud storage',
                    'Priority support',
                    'Advanced analytics'
                ]
            };
        }
    };

    // Handle Stripe checkout
    const handleStripeCheckout = async (e) => {
        e.preventDefault();
        setError('');
        
        // Validation
        const requiredFields = ['firstName', 'lastName', 'email'];
        for (const field of requiredFields) {
            if (!billingInfo[field]) {
                setError(`Please fill in your ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}.`);
                return;
            }
        }

        setLoading(true);
        
        try {
            const { buildUrl } = await import('../../services/api');
            const planDetails = getPlanDetails();
            
            // Debug: Log the data being sent
            const requestData = {
                plan_type: planData.plan,
                billing_info: billingInfo,
                price: planDetails.price,
                billing_cycle: planDetails.billingCycle, // Dynamic based on toggle
                seats: planData.plan === 'family' ? (planDetails.seats || 5) : undefined,
                success_url: `${window.location.origin}/dashboard`,
                cancel_url: `${window.location.origin}/checkout?payment=cancelled`
            };
            
            console.log('Sending checkout request:', requestData);
            console.log('API Base URL:', buildUrl(''));
            console.log('Access Token:', localStorage.getItem('access_token') ? 'Present' : 'Missing');
            
            // Create Stripe checkout session
            const response = await fetch(buildUrl('/subscription/create-checkout-session'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify(requestData),
            });

            console.log('Checkout response status:', response.status);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Checkout response error:', errorText);
                setError(`Server error (${response.status}): ${errorText}`);
                return;
            }

            const data = await response.json();
            console.log('Checkout response data:', data);

            if (data.checkout_url) {
                // Redirect to Stripe checkout
                window.location.href = data.checkout_url;
            } else {
                setError(data.detail || 'Failed to create checkout session. Please try again.');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                setError('Unable to connect to server. Please check if the backend is running.');
            } else {
                setError('Network error. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!planData) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-400 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    const planDetails = getPlanDetails();

    return (
        <React.Fragment>
            <section className="relative py-20 min-h-screen">
                <img className="hidden lg:block absolute top-0 left-0 mt-16 z-10" src="zeus-assets/icons/dots/blue-dot-left-bars.svg" alt />
                <div className="absolute top-0 left-0 bottom-0 w-full bg-gray-50" />
                <div className="relative container px-4 mx-auto">
                    <div className="flex flex-col items-center justify-center">
                        <div className="w-full max-w-lg px-4 mb-12 text-center">
                            <h2 className="mb-10 text-4xl font-semibold font-heading">Complete Your Purchase</h2>
                            <p className="text-xl text-gray-500 mb-8">You're just one step away from unlocking the power of intelligent organization.</p>
                            
                            {/* Billing Cycle Toggle */}
                            <div className="max-w-max mx-auto p-1 bg-gray-50 rounded">
                                <button 
                                    onClick={() => setIsAnnual(false)}
                                    className={`px-8 py-3 text-sm font-semibold leading-normal rounded transition duration-200 ${
                                        !isAnnual ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                                    }`}
                                >
                                    Monthly
                                </button>
                                <button 
                                    onClick={() => setIsAnnual(true)}
                                    className={`px-8 py-3 text-sm font-semibold leading-normal rounded transition duration-200 ${
                                        isAnnual ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                                    }`}
                                >
                                    Annual
                                </button>
                            </div>
                        </div>
                        
                        <div className="w-full max-w-6xl px-4">
                            <div className="grid lg:grid-cols-2 gap-8">
                                {/* Order Summary */}
                                <div className="order-2 lg:order-1">
                                    <div className="p-6 lg:p-8 bg-white border rounded-xl">
                                        <h3 className="text-2xl font-semibold font-heading mb-6">Order Summary</h3>
                                        
                                        <div className="border rounded-lg p-4 mb-6">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h4 className="text-lg font-semibold">{planDetails.name}</h4>
                                                    <p className="text-gray-600 text-sm">{planDetails.description}</p>
                                                    {null}
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-xl font-bold">${planDetails.price}</div>
                                                    <div className="text-sm text-gray-500">
                                                        {isAnnual ? 'per year' : 'per month'}
                                                    </div>
                                                    {isAnnual && (
                                                        <div className="text-xs text-green-600 font-medium">
                                                            ${planDetails.monthlyPrice}/month billed annually
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <ul className="space-y-2">
                                                {planDetails.features.map((feature, index) => (
                                                    <li key={index} className="flex items-center text-sm text-gray-600">
                                                        <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        
                                        <div className="border-t pt-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-gray-600">Subtotal</span>
                                                <span>${planDetails.price}</span>
                                            </div>
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-gray-600">Tax</span>
                                                <span>As applicable</span>
                                            </div>
                                            <div className="flex justify-between items-center text-lg font-semibold border-t pt-2">
                                                <span>Total</span>
                                                <span>${planDetails.price}/{isAnnual ? 'year' : 'month'}</span>
                                            </div>
                                            {null}
                                        </div>
                                        
                                        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                                            <div className="flex items-center">
                                                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span className="text-sm text-blue-800">30-day money-back guarantee</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Billing Information */}
                                <div className="order-1 lg:order-2">
                                    <div className="p-6 lg:p-8 bg-white border rounded-xl">
                                        <form onSubmit={handleStripeCheckout}>
                                            <h3 className="text-2xl font-semibold font-heading mb-6">Billing Information</h3>
                                            
                                                                        {error && (
                                <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                                    {error}
                                </div>
                            )}

                            {success && (
                                <div className="mb-6 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                                    {success}
                                </div>
                            )}

                                            <div className="grid grid-cols-2 gap-4 mb-6">
                                                <div className="relative">
                                                    <input 
                                                        className="relative w-full py-4 pl-4 text-sm border rounded" 
                                                        type="text" 
                                                        name="firstName"
                                                        value={billingInfo.firstName}
                                                        onChange={handleBillingChange}
                                                        placeholder="John" 
                                                        required
                                                    />
                                                    <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white text-gray-500 text-xs">First Name</span>
                                                </div>
                                                <div className="relative">
                                                    <input 
                                                        className="relative w-full py-4 pl-4 text-sm border rounded" 
                                                        type="text" 
                                                        name="lastName"
                                                        value={billingInfo.lastName}
                                                        onChange={handleBillingChange}
                                                        placeholder="Doe" 
                                                        required
                                                    />
                                                    <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white text-gray-500 text-xs">Last Name</span>
                                                </div>
                                            </div>

                                            <div className="relative mb-6">
                                                <input 
                                                    className="relative w-full py-4 pl-4 text-sm border rounded" 
                                                    type="email" 
                                                    name="email"
                                                    value={billingInfo.email}
                                                    onChange={handleBillingChange}
                                                    placeholder="john.doe@example.com" 
                                                    required
                                                />
                                                <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white text-gray-500 text-xs">Email Address</span>
                                            </div>

                                            <div className="relative mb-6">
                                                <input 
                                                    className="relative w-full py-4 pl-4 text-sm border rounded" 
                                                    type="text" 
                                                    name="address"
                                                    value={billingInfo.address}
                                                    onChange={handleBillingChange}
                                                    placeholder="123 Main Street" 
                                                />
                                                <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white text-gray-500 text-xs">Address (Optional)</span>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4 mb-6">
                                                <div className="relative">
                                                    <input 
                                                        className="relative w-full py-4 pl-4 text-sm border rounded" 
                                                        type="text" 
                                                        name="city"
                                                        value={billingInfo.city}
                                                        onChange={handleBillingChange}
                                                        placeholder="New York" 
                                                    />
                                                    <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white text-gray-500 text-xs">City</span>
                                                </div>
                                                <div className="relative">
                                                    <input 
                                                        className="relative w-full py-4 pl-4 text-sm border rounded" 
                                                        type="text" 
                                                        name="state"
                                                        value={billingInfo.state}
                                                        onChange={handleBillingChange}
                                                        placeholder="NY" 
                                                    />
                                                    <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white text-gray-500 text-xs">State</span>
                                                </div>
                                            </div>

                                            <div className="mb-8">
                                                <h4 className="text-lg font-semibold mb-4">Payment Method</h4>
                                                <div className="border rounded-lg p-4">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center">
                                                            <svg className="w-8 h-8 mr-3" viewBox="0 0 24 24">
                                                                <rect x="2" y="6" width="20" height="12" rx="2" fill="#1a56db"/>
                                                                <rect x="2" y="8" width="20" height="2" fill="#ffffff"/>
                                                                <rect x="2" y="12" width="4" height="2" fill="#ffffff"/>
                                                            </svg>
                                                            <span className="font-medium">Secure Credit Card Payment</span>
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <svg className="w-8 h-6" viewBox="0 0 36 24" fill="none">
                                                                <rect width="36" height="24" rx="4" fill="#0066CC"/>
                                                                <path d="M8 8h20v8H8z" fill="#FFF"/>
                                                            </svg>
                                                            <svg className="w-8 h-6" viewBox="0 0 36 24" fill="none">
                                                                <rect width="36" height="24" rx="4" fill="#FF5F00"/>
                                                                <circle cx="14" cy="12" r="6" fill="#EB001B"/>
                                                                <circle cx="22" cy="12" r="6" fill="#F79E1B"/>
                                                            </svg>
                                                        </div>
                                                    </div>
                                                    <p className="text-sm text-gray-600 mt-2">
                                                        You'll be redirected to Stripe's secure checkout to complete your payment.
                                                    </p>
                                                </div>
                                            </div>

                                            <button 
                                                type="submit" 
                                                disabled={loading}
                                                className={`w-full py-4 mb-4 text-sm text-white font-medium leading-normal rounded transition duration-200 text-center ${
                                                    loading 
                                                        ? 'bg-gray-400 cursor-not-allowed' 
                                                        : 'bg-red-400 hover:bg-red-300'
                                                }`}
                                            >
                                                {loading ? 'Processing...' : `Complete Purchase - $${planDetails.price}/${isAnnual ? 'year' : 'month'}`}
                                            </button>

                                            <div className="text-center text-xs text-gray-500">
                                                <p className="mb-2">By completing your purchase, you agree to our <a href="/terms-and-conditions" className="text-red-400 hover:underline">Terms of Service</a></p>
                                                <p>Powered by <span className="font-semibold">Stripe</span> - Your payment information is secure and encrypted.</p>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </React.Fragment>
    );
}
